import { MutableRefObject, useState } from 'react';
import PropertySearchField from 'components/forms/domain/property/PropertySearchField/PropertySearchField';
import Switch from 'components/switch/Switch';
import {
  GetIntegrationAgencySettingsResponseTO,
  IntegrationEnum,
} from 'models/InternalIntegrationSetting';
import { useTranslation } from 'react-i18next';
import { AsyncTypeAheadFieldInstance } from 'components/forms/typeAheadField/AsyncTypeAheadField.types';
import FilterIconWithCounterFromForm from 'components/filters/FilterIconWithCounterFromForm';
import { PROPERTY_STATUS_FILTER_COUNTER_OVERRIDES } from 'components/forms/domain/property/StatusSelectField.constants';
import useEnableIntegrationMutation from 'pages/integrations/hooks/useEnableIntegrationMutation';
import useDisableIntegrationMutation, {
  IntegrationMutationLevel,
} from 'pages/integrations/hooks/useDisableIntegrationMutation';
import { queryClient } from 'queryClient';
import { getIntegrationPropertySettingsQueryKey } from 'hooks/useFetchIntegrationPropertySettings';
import useFetchPropertiesActivity, {
  getPropertiesActivityQueryKey,
} from '../useFetchPropertiesActivity';
import PropertiesFilter from './PropertiesFilter';
import {
  ActionsWrapper,
  HeaderWithFiltersContainer,
  PageHeaderStyled,
  PropertiesFilterFormContainer,
  SwitchContainer,
} from './HeaderWithFilters.styles';
import DataReadAccessCheckbox from './dataReadAccessCheckbox/DataReadAccessCheckbox';

interface HeaderWithFiltersProps {
  integrationAgencySettings: GetIntegrationAgencySettingsResponseTO;
  onFilterClear: () => void;
  propertySearchFieldRef: MutableRefObject<AsyncTypeAheadFieldInstance>;
}

const HeaderWithFilters = ({
  integrationAgencySettings: { enumId, agencySettings, integration },
  onFilterClear,
  propertySearchFieldRef,
}: HeaderWithFiltersProps) => {
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const toggleFilter = () => {
    setIsFilterOpen((current) => !current);
  };

  const { data: propertiesActivity, isLoading: isLoadingPropertiesActivity } =
    useFetchPropertiesActivity({ enumId });

  const { disableIntegration, isLoading: isDisablingIntegration } =
    useDisableIntegrationMutation();

  const { enableIntegration, isLoading: isEnablingIntegration } =
    useEnableIntegrationMutation();

  const isAllPropertiesEnabled =
    propertiesActivity?.allAvailablePropertiesActive;

  const propertiesActivityQueryKey = getPropertiesActivityQueryKey({ enumId });

  const handleToggleAllProperties = () => {
    const params = {
      level: IntegrationMutationLevel.PROPERTY as const,
      enumId,
      propertyUids: [],
      appliesToAllProperties: true,
      queryKey: getIntegrationPropertySettingsQueryKey({
        enumId,
        propertyUids: [],
      }),
    };

    const onSuccess = () => {
      queryClient.invalidateQueries({
        queryKey: propertiesActivityQueryKey,
      });
    };

    if (isAllPropertiesEnabled) {
      disableIntegration(params, { onSuccess });
    } else {
      enableIntegration(params, { onSuccess });
    }
  };

  const isLoading =
    isDisablingIntegration ||
    isEnablingIntegration ||
    isLoadingPropertiesActivity;

  const shouldShowToggleAllProperties = ![
    IntegrationEnum.POINTCENTRAL,
    IntegrationEnum.REMOTELOCK,
  ].includes(enumId);

  return (
    <HeaderWithFiltersContainer data-testid="properties-list-header">
      <PageHeaderStyled
        title={t('pageIntegrations.integrationDetails.properties')}
      >
        <ActionsWrapper>
          <DataReadAccessCheckbox
            agencySettings={agencySettings}
            integration={integration}
          />

          <div>
            <PropertySearchField
              align="right"
              containerClassName="property-search-field"
              name="propertySearch"
              instanceRef={propertySearchFieldRef}
            />

            <FilterIconWithCounterFromForm
              isFilterOpen={isFilterOpen}
              onClick={toggleFilter}
              counterOverrides={PROPERTY_STATUS_FILTER_COUNTER_OVERRIDES}
            />

            {shouldShowToggleAllProperties && (
              <SwitchContainer>
                {t('pageIntegrations.integrationDetails.toggleAll')}
                <Switch
                  checked={isAllPropertiesEnabled}
                  disabled={isLoading}
                  onChange={handleToggleAllProperties}
                  data-testid="toggle-all-properties"
                />
              </SwitchContainer>
            )}
          </div>
        </ActionsWrapper>
      </PageHeaderStyled>

      <PropertiesFilterFormContainer isCollapsed={!isFilterOpen}>
        <PropertiesFilter onClear={onFilterClear} />
      </PropertiesFilterFormContainer>
    </HeaderWithFiltersContainer>
  );
};

export default HeaderWithFilters;
